// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { trackAsImpersonation, trackGaButtonClickEvent } from "@stoneage/App/Utilities/DataLayer";
import sortBy from "lodash/sortBy";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    fields: {
        title: string;
        newsType: string;
        colorTheme: string;
        parentNewsPage: LinkFieldValue;
        viewAllTitle: string;
        openInNewTab: boolean;
        titleStyle: string;
        numberOfNews: number;
        gaClickEvent: string;
        gaBtnClickEventName: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory & OwnProps;

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const pages = getDataView(state.data.pages, { parentNodeId: ownProps.fields.parentNewsPage.value });
    const parentPageLink = getPageLinkByNodeId(state, ownProps.fields.parentNewsPage.value);
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }

    return {
        pages,
        links,
        parentPageLink,
        session: state.context.session,
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
};

export interface FeaturedNewsStyles {
    widgetContainer?: InjectableCss;
    infoWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const featuredNewsStyles: FeaturedNewsStyles = {
    widgetContainer: {
        css: css`
            padding: 20px;
            box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
            border-radius: 2px;
            display: flex;
            flex-direction: column;
            box-shadow: inset 4px 0 0 0 ${getColor("common.ui6")};
            height: 100%;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 20px;
        `,
    },
    link: {
        css: css`
            span {
                display: flex;
                align-items: center;
            }
            svg {
                width: 14px;
                height: 14px;
            }
        `,
        icon: {
            iconProps: { src: "ChevronRight" },
            position: "right",
        },
    },
    title: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    linkWrapper: {
        css: css`
            margin-top: auto;
        `,
    },
};

const styles = featuredNewsStyles;

const today = new Date().getTime();

const comparePublishDate = (page: PageProps) => {
    const publishDate = new Date(page.fields["publishDate"]).getTime();

    return publishDate <= today;
};

const compareExpirationDate = (page: PageProps) => {
    const expirationDate = new Date(page.fields["expirationDate"]).getTime();

    return expirationDate > today;
};

const NewsListView = ({
    pages,
    links,
    loadPagesForParent,
    id,
    parentPageLink,
    session,
    fields: {
        title,
        newsType,
        colorTheme,
        parentNewsPage,
        viewAllTitle,
        titleStyle,
        openInNewTab,
        numberOfNews,
        gaClickEvent,
        gaBtnClickEventName,
    },
    impersonation,
}: Props) => {
    useEffect(() => {
        if (!pages?.value && !pages?.isLoading) {
            loadPagesForParent({
                page: 1,
                pageSize: 999,
                parentNodeId: parentNewsPage.value,
            });
        }
    }, []);

    const filteredPages = pages?.value?.filter(page => page.generalFields.newsPageType === newsType);

    if (!filteredPages || !filteredPages.length) {
        return null;
    }
    let sortedAndPagedPages = sortBy(filteredPages, o => o.fields["publishDate"]);
    sortedAndPagedPages.reverse();

    sortedAndPagedPages = sortedAndPagedPages.filter(page => comparePublishDate(page));
    sortedAndPagedPages = sortedAndPagedPages.filter(page => compareExpirationDate(page));

    const getTextColor = () => (colorTheme === "light" ? "common.black" : "common.white");
    const getBackgroundColor = () => (colorTheme === "light" ? "common.white" : "common.ui10");

    return (
        <StyledWrapper
            {...styles.widgetContainer}
            css={css`
                ${styles.widgetContainer?.css};
                background-color: ${getColor(getBackgroundColor())};
            `}
        >
            {title && (
                <Typography {...styles.title} variant={titleStyle as keyof ThemeTypography} color={getTextColor()}>
                    {title}
                </Typography>
            )}
            <StyledWrapper {...styles.infoWrapper}>
                {sortedAndPagedPages?.length > 0 &&
                    sortedAndPagedPages.slice(0, numberOfNews).map((page, index) => {
                        let dateDisplay = "";
                        if (page.fields["publishDate"]) {
                            dateDisplay = getLocalizedDateTime({
                                dateTime: new Date(page.fields["publishDate"]),
                                language: session.language,
                                options: {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                },
                            });
                        }
                        return (
                            <StyledWrapper
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    margin-bottom: 16px;
                                `}
                            >
                                <Typography
                                    color={colorTheme === "light" ? "text.main" : "common.ui5"}
                                    size={12}
                                    lineHeight="18px"
                                >
                                    {translate("Updated")}&nbsp;|&nbsp;
                                    {dateDisplay}&nbsp;|&nbsp;
                                    {page.fields["author"]}
                                </Typography>
                                <Link
                                    color={colorTheme === "light" ? "primary" : "common.white"}
                                    href={links[page.nodeId]?.url}
                                    target="_blank"
                                    {...(gaClickEvent &&
                                        gaBtnClickEventName && {
                                            onClick: () => {
                                                trackGaButtonClickEvent(
                                                    gaBtnClickEventName,
                                                    page.fields["title"],
                                                    impersonation,
                                                    undefined,
                                                    links[page.nodeId]?.url,
                                                );
                                            },
                                        })}
                                >
                                    {page.fields["title"]}
                                </Link>
                            </StyledWrapper>
                        );
                    })}
            </StyledWrapper>
            <StyledWrapper {...styles.linkWrapper}>
                <Link
                    href={parentPageLink?.url}
                    target={openInNewTab ? "_blank" : ""}
                    {...styles.link}
                    color={colorTheme === "light" ? "primary" : "common.white"}
                    {...(gaClickEvent &&
                        gaBtnClickEventName && {
                            onClick: () => {
                                trackGaButtonClickEvent(
                                    gaBtnClickEventName,
                                    viewAllTitle || parentPageLink?.title || "",
                                    impersonation,
                                    undefined,
                                    parentPageLink?.url,
                                );
                            },
                        })}
                >
                    {viewAllTitle || parentPageLink?.title}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView)),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Featured News Card",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: "titleStyle",
                displayName: "Title Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: "viewAllTitle",
                displayName: "View All Link Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "openInNewTab",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: "parentNewsPage",
                displayName: "Select Parent News Page",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
            {
                name: "newsType",
                displayName: "News Type",
                editorTemplate: "SystemListDropDownField",
                defaultValue: "",
                fieldType: "General",
                systemListName: "NewsPageType",
            },
            {
                name: "numberOfNews",
                displayName: "Number of News to Display",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 2,
                fieldType: "General",
            },
            {
                name: "colorTheme",
                displayName: "Theme Color",
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            },
            {
                name: "gaClickEvent",
                displayName: "Track GA4 Buttons Click Event",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: "gaBtnClickEventName",
                displayName: "Btn Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: item => item.fields["gaClickEvent"],
            },
        ],
    },
};

export default widgetModule;
